import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Form, Input, Select, DatePicker, Row, Col } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import styled from "styled-components";
import writeIcon from "../../assets/svg/write_icon.svg";
import {
  SUBJECT_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
} from "./constants";
import { useTaskModal } from "../../hooks/useTaskModal";
import {
  CreateTaskConfig,
  TaskStatus,
  TaskPriority,
  SubjectType,
} from "../../models/workflowModels";
import { PathToResolution } from "./PathToResolution";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
} from "../../components/common/Modal";
import { greyText } from "../../styles";

const FormContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  border-radius: 10px;
  padding-top: 16px;
`;

const StyledFormWrapper = styled.div`
  width: 100%;

  .ant-select-selector,
  .ant-picker,
  .ant-input {
    width: 100%;
  }

  .ant-form-item-label > label {
    color: ${greyText} !important;
  }
`;

interface TaskFormValues {
  title: string;
  assignee: string;
  priority: TaskPriority;
  dueDate?: Dayjs;
  subjectType?: SubjectType;
  status?: TaskStatus;
}

export const TaskModal = ({
  onTaskAdd,
  onTaskUpdate,
  onHide,
  shouldDisplay,
}: TaskModalProps): JSX.Element => {
  const [form] = Form.useForm<TaskFormValues>();
  const [searchParams] = useSearchParams();
  const {
    dispatchResetParams,
    createTaskConfig,
    isUsersLoading,
    isTaskLoading,
    userOptions,
    taskConfigOverrides,
    taskStatus,
  } = useTaskModal();

  const taskId = searchParams.get("taskId");
  const isViewTask = taskId !== null;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (shouldDisplay && isViewTask && taskConfigOverrides) {
      // Parse date carefully - only set if it's a valid date string
      let dueDateValue: Dayjs | undefined;
      const dateStr = taskConfigOverrides.date?.displayValue;
      if (
        dateStr &&
        dateStr !== "Select due date" &&
        dayjs(dateStr, "YYYY-MM-DD", true).isValid()
      ) {
        dueDateValue = dayjs(dateStr, "YYYY-MM-DD");
      }

      form.setFieldsValue({
        title: createTaskConfig.title || "",
        assignee: taskConfigOverrides.assignee?.key || undefined,
        priority:
          (taskConfigOverrides.priority?.key as TaskPriority) || undefined,
        dueDate: dueDateValue,
        status: (taskConfigOverrides.status?.key as TaskStatus) || undefined,
      });
    } else if (!shouldDisplay) {
      form.resetFields();
    }
  }, [
    shouldDisplay,
    isViewTask,
    taskConfigOverrides,
    createTaskConfig.title,
    form,
  ]);

  const handleModalHide = (): void => {
    dispatchResetParams();
    form.resetFields();
    onHide();
  };

  // Simple option arrays for Ant Design Select - uses built-in filtering
  const assigneeOptions = userOptions.map((user) => ({
    value: user.key,
    label: user.displayValue,
  }));

  const onFinish = (values: TaskFormValues): void => {
    // Build the task config directly from form values (not from state which updates async)
    const taskConfig: CreateTaskConfig = {
      key: createTaskConfig.key,
      title: values.title,
      assignee: values.assignee,
      priority: values.priority,
      dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null,
      subjectType: values.subjectType || createTaskConfig.subjectType,
      subjects: createTaskConfig.subjects,
    };

    if (isViewTask) {
      const statusToUse = values.status || taskStatus;
      onTaskUpdate(taskConfig, taskId, statusToUse as TaskStatus);
    } else {
      onTaskAdd(taskConfig);
    }
    handleModalHide();
  };

  const footerContent = (
    <ModalFooter
      onClose={handleModalHide}
      onSubmit={(): void => form.submit()}
      closeText="Cancel"
      submitText={isViewTask ? "Update Task" : "Create Task"}
      isLoading={isTaskLoading}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      onHide={handleModalHide}
      show={shouldDisplay}
      size="lg"
      title={isViewTask ? "View Task" : "Add Task"}
      titleIcon={writeIcon}
      modalType="lg"
      footer={footerContent}
    >
      <ModalContent>
        <FormContainer>
          <StyledFormWrapper>
            <Form<TaskFormValues>
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onKeyDown={(e): void => {
                if (
                  e.key === "Enter" &&
                  (e.target as HTMLElement).tagName !== "TEXTAREA"
                ) {
                  e.preventDefault();
                }
              }}
              size="large"
              requiredMark
            >
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: "Please enter a task title" },
                ]}
              >
                <Input
                  placeholder="Enter task title"
                  disabled={isTaskLoading}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="assignee"
                    label="Assignee"
                    rules={[
                      { required: true, message: "Please select an assignee" },
                    ]}
                  >
                    <Select
                      placeholder="Select assignee"
                      options={assigneeOptions}
                      loading={isUsersLoading || isTaskLoading}
                      disabled={isUsersLoading || isTaskLoading}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    rules={[
                      { required: true, message: "Please select a priority" },
                    ]}
                  >
                    <Select
                      placeholder="Select priority"
                      options={PRIORITY_OPTIONS}
                      disabled={isTaskLoading}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="dueDate" label="Due Date">
                    <DatePicker
                      style={{ width: "100%" }}
                      disabled={isTaskLoading}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  {isViewTask ? (
                    <Form.Item name="status" label="Status">
                      <Select
                        placeholder="Select status"
                        options={STATUS_OPTIONS}
                        disabled={isTaskLoading}
                        allowClear
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item name="subjectType" label="Subject Type">
                      <Select
                        placeholder="Select subject type"
                        options={SUBJECT_TYPE_OPTIONS}
                        allowClear
                      />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Form>
            <PathToResolution />
          </StyledFormWrapper>
        </FormContainer>
      </ModalContent>
    </CompassModal>
  );
};

export type TaskModalProps = {
  onTaskAdd: (params: CreateTaskConfig) => void;
  onTaskUpdate: (
    params: CreateTaskConfig,
    taskId: string,
    status: TaskStatus,
  ) => void;
  shouldDisplay: boolean;
  onHide: () => void;
};
